<template>
  <section class="scard" :class="[`scard--${tone}`, { 'scard--dense': dense }]">
    <header v-if="title || $slots['header-extra']" class="scard__head">
      <h3 class="scard__title">
        {{ title }}
        <span v-if="count != null && count > 0" class="scard__count">{{ count }}</span>
      </h3>
      <div class="scard__head-extra">
        <slot name="header-extra" />
        <button v-if="to" type="button" class="scard__action" @click="onAction">
          {{ actionLabel }}
          <el-icon><ArrowRight /></el-icon>
        </button>
      </div>
    </header>

    <div class="scard__body">
      <slot>
        <div v-if="emptyText" class="scard__empty">{{ emptyText }}</div>
      </slot>
    </div>
  </section>
</template>

<script>
import { useRouter } from 'vue-router'
import { ArrowRight } from '@element-plus/icons-vue'

/**
 * 카드 껍데기 단일 구현.
 *
 * 대시보드 카드 7개가 각자 .content-card / .card-header / .card-body 를 복제하고 있었고,
 * 그중 셋(MyTasksSummary, UnassignedTasks, UpcomingDueTasks)은 background:#fff 와
 * box-shadow 를 하드코딩해서 토큰을 쓰는 나머지와 미묘하게 달라 보였다.
 */
export default {
  name: 'SectionCard',
  components: { ArrowRight },
  props: {
    title: { type: String, default: '' },
    count: { type: Number, default: null },
    to: { type: [String, Object], default: null },
    actionLabel: { type: String, default: '전체보기' },
    // 좌측 액센트 바
    tone: { type: String, default: 'default' }, // default | warn | danger | success
    dense: { type: Boolean, default: false },
    emptyText: { type: String, default: '' }
  },
  emits: ['action'],
  setup(props, { emit }) {
    const router = useRouter()
    function onAction() {
      emit('action')
      if (props.to) router.push(props.to)
    }
    return { onAction }
  }
}
</script>

<style scoped>
.scard {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--r-lg);
  box-shadow: var(--sh-1);
  margin-bottom: var(--sp-3);
  overflow: hidden;
}

.scard--warn { border-left: 3px solid var(--color-warning); }
.scard--danger { border-left: 3px solid var(--color-danger); }
.scard--success { border-left: 3px solid var(--color-success); }

.scard__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-2);
  padding: var(--sp-4) var(--sp-4) 0;
}

.scard__title {
  margin: 0;
  font-size: var(--fs-md);
  letter-spacing: -0.01em;
  font-weight: var(--fw-semibold);
  color: var(--color-text);
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  min-width: 0;
}

/* 개수 배지는 카드의 tone 을 따라간다. 결재 대기(warn) 카드의 '3' 이
   파랗게 나오면 카드 왼쪽의 앰버 액센트와 서로 다른 말을 하게 된다. */
.scard__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 19px;
  height: 19px;
  padding: 0 6px;
  border-radius: var(--r-full);
  background: var(--color-primary-soft);
  color: var(--color-primary-strong);
  font-family: var(--font-num);
  font-variant-numeric: tabular-nums;
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
}

.scard--warn .scard__count { background: var(--color-warning-soft); color: var(--color-warning-strong); }
.scard--danger .scard__count { background: var(--color-danger-soft); color: var(--color-danger-strong); }
.scard--success .scard__count { background: var(--color-success-soft); color: var(--color-success-strong); }

.scard__head-extra {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex-shrink: 0;
}

.scard__action {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: 0;
  background: none;
  color: var(--color-text-muted);
  font-family: inherit;
  font-size: var(--fs-sm);
  cursor: pointer;
  padding: var(--sp-2);
  margin: calc(var(--sp-2) * -1);
  border-radius: var(--r-sm);
  min-height: var(--tap-min);
}

.scard__action:active { background: var(--color-surface-2); }

.scard__body { padding: var(--sp-3) var(--sp-4) var(--sp-4); }
.scard--dense .scard__body { padding: var(--sp-2) var(--sp-3) var(--sp-3); }

.scard__empty {
  padding: var(--sp-6) 0;
  text-align: center;
  color: var(--color-text-subtle);
  font-size: var(--fs-sm);
}

@media (max-width: 767px) { /* --bp-mobile-max */
  .scard__head { padding: var(--sp-3) var(--sp-3) 0; }
  .scard__body { padding: var(--sp-2) var(--sp-3) var(--sp-3); }
  .scard__title { font-size: var(--fs-md); }
  .scard { margin-bottom: var(--sp-3); }
}
</style>
